/**
 * StickyObserver is a utility class that observes an element with position sticky and toggles a CSS class
 */
class StickyObserver {
	/**
	 * @property {HTMLElement|string} element - The element to observe. Can be a selector string or an HTMLElement.
	 * @private
	 */
	#element;

	/**
	 * @property {string} cssClass - The CSS class to toggle when the element becomes sticky.
	 * @private
	 */
	#cssClass;

	/**
	 * @property {CSSStyleDeclaration} stickyElementStyle - The computed style of the sticky element.
	 * @private
	 */
	#stickyElementStyle = null;

	/**
	 * @property {number} stickyElementTop - The top offset of the sticky element.
	 * @private
	 */
	#stickyElementTop = 0;

	/**
	 * Creates an instance of StickyObserver.
	 * @param {string|HTMLElement} element - The element to observe. Can be a selector string or an HTMLElement.
	 * @param {string} cssClass - The CSS class to toggle when the element becomes sticky. Defaults to "is-sticky".
	 */
	constructor(element, cssClass = "is-sticky") {
		this.#cssClass = cssClass;
		this.#element = this.#getElement(element);
		this.#init();
	}

	/**
	 * Initializes the StickyObserver by adding a scroll event listener.
	 * @private
	 * @returns {void}
	 */
	#init() {
		window.addEventListener("scroll", () => {
			this.#determineStickyState(this.#element);
		});
	}

	/**
	 * Helper method to get the element from a selector string or an HTMLElement.
	 * @param {string|HTMLElement} element
	 * @returns {HTMLElement|never} - The HTMLElement if found, or throws an error if not provided.
	 * @private
	 * @throws {Error} - Throws an error if the element is not provided or not found.
	 */
	#getElement(element) {
		if (!element) {
			throw new Error("Element is required for StickyObserver");
		}

		if (typeof element === "string") {
			return document.querySelector(element);
		}
		return element;
	}

	/**
	 * Determines the sticky state of the element and toggles the CSS class accordingly.
	 * @param {HTMLElement} element - The element to observe for sticky state.
	 * @private
	 * @returns {void}
	 */
	#determineStickyState(element) {
		//  Get the top offset of the sticky element if not already set
		if (!this.#stickyElementStyle) {
			this.#stickyElementStyle = window.getComputedStyle(element);
			this.#stickyElementTop = parseInt(this.#stickyElementStyle.top, 10);
		}

		const currentTop = element.getBoundingClientRect().top;

		// Toggle the CSS class based on the current position of the element
		element.classList.toggle(
			this.#cssClass,
			currentTop <= this.#stickyElementTop,
		);

		// If the element is at the top of the page, remove the sticky class
		if (window.scrollY === 0) {
			element.classList.remove(this.#cssClass);
		}
	}
}

export default StickyObserver;
